import type {
  AnchorHTMLAttributes,
  ComponentPropsWithoutRef,
  CSSProperties,
  ReactNode,
} from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../lib'
import { designTokens } from '../lib/design-tokens'
import { typographyStyle } from '../lib/catalog'
import { textLinkVariants } from './text-link-variants'
import type {
  TextLinkAlign,
  TextLinkSize,
  TextLinkUnderline,
  TextLinkVariantId,
} from './text-link.types'

type BaseProps = {
  variant?: TextLinkVariantId
  size?: TextLinkSize
  underline?: TextLinkUnderline
  align?: TextLinkAlign
  className?: string
  children: ReactNode
}

type TextLinkInternalProps = BaseProps &
  Omit<ComponentPropsWithoutRef<typeof Link>, 'to' | 'className' | 'children'> & {
    to: string
    href?: never
    external?: never
  }

type TextLinkExternalProps = BaseProps &
  Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'className' | 'children' | 'target' | 'rel'
  > & {
    href: string
    to?: never
    external?: boolean
    target?: string
    rel?: string
  }

export type TextLinkProps = TextLinkInternalProps | TextLinkExternalProps
type TextLinkRestProps = Omit<
  TextLinkProps,
  'variant' | 'size' | 'underline' | 'align' | 'className' | 'children'
>
type TextLinkExternalRestProps = Omit<
  TextLinkExternalProps,
  'variant' | 'size' | 'underline' | 'align' | 'className' | 'children'
>

const sizeTokens: Record<TextLinkSize, { size: string; lineHeight: string; weight: number }> = {
  sm: designTokens.typography.body.sm,
  md: designTokens.typography.body.md,
  lg: designTokens.typography.body.lg,
}

const underlineClasses: Record<TextLinkUnderline, string> = {
  always: 'underline underline-offset-2',
  hover: 'hover:underline hover:underline-offset-2',
  none: 'no-underline',
}

const alignClasses: Record<TextLinkAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

export function TextLink(props: TextLinkProps) {
  const { variant = 'primary', size, underline, align, className, children, ...rest } = props
  const isExternalLink = (value: TextLinkRestProps): value is TextLinkExternalRestProps =>
    'href' in value && typeof value.href === 'string'
  const variantConfig = variant ? textLinkVariants[variant] : undefined
  const resolvedSize = size ?? variantConfig?.size ?? 'md'
  const resolvedUnderline = underline ?? variantConfig?.underline ?? 'hover'
  const resolvedAlign = align ?? variantConfig?.align ?? 'left'
  const style: CSSProperties = {
    ...typographyStyle(sizeTokens[resolvedSize], {
      lineHeight: variantConfig?.lineHeight,
      fontWeight: variantConfig?.fontWeight,
    }),
    ...(variantConfig?.fontFamily ? { fontFamily: variantConfig.fontFamily } : {}),
    ...(variantConfig
      ? ({
          '--text-link-color': variantConfig.color,
          '--text-link-hover': variantConfig.hoverColor,
          '--text-link-pressed': variantConfig.pressedColor,
        } as CSSProperties)
      : {}),
  }
  const classes = cn(
    'inline-flex items-center gap-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    variantConfig
      ? 'text-[var(--text-link-color)] hover:text-[var(--text-link-hover)] active:text-[var(--text-link-pressed)]'
      : 'text-primary hover:text-primary/80 active:text-primary/70',
    underlineClasses[resolvedUnderline],
    alignClasses[resolvedAlign],
    className
  )

  if (isExternalLink(rest)) {
    const { href, external, target, rel, ...restProps } = rest
    const isExternal = external ?? /^https?:\/\//.test(href)
    const resolvedTarget = isExternal ? (target ?? '_blank') : target
    const resolvedRel = isExternal ? (rel ?? 'noopener noreferrer') : rel

    return (
      <a
        href={href}
        className={classes}
        style={style}
        target={resolvedTarget}
        rel={resolvedRel}
        {...restProps}
      >
        {children}
      </a>
    )
  }

  const { to, ...restProps } = rest
  return (
    <Link to={to} className={classes} style={style} {...restProps}>
      {children}
    </Link>
  )
}
